import clsx from "clsx"
import { useEffect, useState } from "react"

import useIsAnyDialogOpen from "@hooks/useIsAnyDialogOpen"
import useIsDesktop from "@hooks/useIsDesktop"
import useUser from "@hooks/useUser"
import useUserNavigationUtils from "@hooks/useUserNavigationUtils"

import extractURLSearchParam from "@utils/extractURLSearchParam"
import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import { UserAvatar } from "@molecules"

import { Menu } from "@organisms"

import { SETTINGS_ROUTES } from "@routes/settings"

import { PROFILE_BOTTOM_SHEET_OPEN } from "@constants/searchParams"

import styles from "./SideBarProfileButton.module.scss"
import { SideBarProfileButtonProps } from "./SideBarProfileButton.types"

const statusFromUrl = extractURLSearchParam(PROFILE_BOTTOM_SHEET_OPEN)

export default function SideBarProfileButton(props: SideBarProfileButtonProps) {
    const {
        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isDisabled = false,
        defaultOpen = false,
    } = props

    const { user } = useUser()
    const [isOpen, setIsOpen] = useState<boolean>(statusFromUrl === "open" || defaultOpen)

    const isDesktop = useIsDesktop()

    const { setIsAnyDialogOpen } = useIsAnyDialogOpen()
    const {
        logout,
        openHelpCenter,
        openChangelog,
        openRequestFeature,
        openContactSupport,
        goToProfile,
        goToSettings,
    } = useUserNavigationUtils()

    useEffect(() => {
        setIsAnyDialogOpen(isOpen)
    }, [isOpen])

    return (
        <Menu
            className={styles.base}
            title={user?.full_name || "Profile"}
            headerLeft={user ? <UserAvatar user={user} size={"sm"} /> : undefined}
            isOpen={isOpen}
            popoverAlign="start"
            popoverSide="bottom"
            setIsOpen={setIsOpen}
            trigger={
                <div
                    role="button"
                    aria-disabled={isDisabled}
                    className={clsx(styles.button, {
                        [styles.isFocused]: _isFocused,
                        [styles.isHovered]: _isHovered,
                        [styles.isPressed]: isPressed || isOpen,
                        [styles.isDisabled]: isDisabled,
                    })}
                    tabIndex={isDisabled ? -1 : 0}
                    onClick={() => setIsOpen(!isOpen)}
                    onKeyDown={(e) => ifSpaceOrEnter(e, () => setIsOpen(!isOpen))}
                >
                    {user && <UserAvatar user={user} size={isDesktop ? "sm" : "md"} />}
                </div>
            }
            items={[
                {
                    type: "group",
                    items: [
                        {
                            text: user?.full_name || "",
                            hideOnMobile: true,
                            type: "labelItem",
                        },
                        {
                            icon: faIcon({ name: "circle-user", style: "regular" }),
                            label: "Profile",
                            path: SETTINGS_ROUTES.profile,
                            onClick: goToProfile,
                        },
                        {
                            icon: faIcon({ name: "gear", style: "regular" }),
                            label: "Settings",
                            path: SETTINGS_ROUTES.main,
                            onClick: goToSettings,
                        },
                    ],
                },
                { type: "divider" },
                {
                    type: "group",
                    items: [
                        {
                            icon: faIcon({ name: "circle-question", style: "regular" }),
                            label: "Help Center",
                            onClick: openHelpCenter,
                        },
                        {
                            icon: faIcon({ name: "headset", style: "regular" }),
                            label: "Contact Support",
                            onClick: openContactSupport,
                        },
                        {
                            icon: faIcon({ name: "circle-info", style: "regular" }),
                            label: "Request Feature",
                            onClick: openRequestFeature,
                        },
                        {
                            icon: faIcon({ name: "bullhorn", style: "regular" }),
                            label: "What's New",
                            onClick: openChangelog,
                        },
                    ],
                },
                { type: "divider" },
                {
                    type: "group",
                    items: [
                        {
                            icon: faIcon({ name: "arrow-right-from-bracket", style: "regular" }),
                            label: "Log Out",
                            variant: "danger",
                            onClick: () => void logout(),
                        },
                    ],
                },
            ]}
        />
    )
}
